import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
      } catch (e) { /* keep polling */ }
      attempts.current += 1;
      if (attempts.current < 8) setTimeout(poll, 2000);
      else setState("failed");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50" data-testid="payment-success-page">
      <Navbar />
      <div className="flex flex-1 items-center justify-center px-4 py-20">
        <div className="card-clean max-w-md p-10 text-center">
          {state === "polling" && (
            <>
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-sky-500" />
              <h1 className="mt-4 font-display text-3xl font-extrabold uppercase text-slate-900">Confirming Payment</h1>
              <p className="mt-2 text-sm text-slate-500">Hold on — we're confirming your payment with Stripe.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="mx-auto h-12 w-12 text-sky-500" data-testid="payment-confirmed-icon" />
              <h1 className="mt-4 font-display text-3xl font-extrabold uppercase text-slate-900" data-testid="payment-confirmed-heading">You're Booked!</h1>
              <p className="mt-2 text-sm text-slate-500">
                Payment confirmed. We'll see you at Exmouth Marina — bring warm layers and we'll bring the fish.
              </p>
              <Link to="/"><Button data-testid="payment-home-button" className="btn-lift mt-6 rounded-full bg-sky-500 font-bold text-white hover:bg-sky-600">Back to Shande III</Button></Link>
            </>
          )}
          {state === "failed" && (
            <>
              <XCircle className="mx-auto h-12 w-12 text-amber-500" />
              <h1 className="mt-4 font-display text-3xl font-extrabold uppercase text-slate-900">Still Processing</h1>
              <p className="mt-2 text-sm text-slate-500">
                We couldn't confirm your payment yet. If money left your account it will confirm shortly — check back soon.
              </p>
              <Link to="/"><Button data-testid="payment-retry-home-button" className="btn-lift mt-6 rounded-full bg-slate-900 font-bold text-white">Back to site</Button></Link>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50" data-testid="payment-cancel-page">
      <Navbar />
      <div className="flex flex-1 items-center justify-center px-4 py-20">
        <div className="card-clean max-w-md p-10 text-center">
          <XCircle className="mx-auto h-12 w-12 text-slate-400" />
          <h1 className="mt-4 font-display text-3xl font-extrabold uppercase text-slate-900" data-testid="payment-cancel-heading">Payment Cancelled</h1>
          <p className="mt-2 text-sm text-slate-500">No charge was made. Your date is still open if you'd like to try again.</p>
          <Link to="/book"><Button data-testid="payment-cancel-book-button" className="btn-lift mt-6 rounded-full bg-sky-500 font-bold text-white hover:bg-sky-600">Back to Booking</Button></Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
